// A text/number box that saves when you finish with it (leave the box or press Enter), not on every keystroke.

import { useEffect, useState, type InputHTMLAttributes } from 'react';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange'> {
  /** What is saved right now. */
  value: string;
  /**
   * Called with the new text when it differs from `value`. Resolve to `false` if it was rejected: the box then goes
   * back to the saved value instead of showing something that isn't true.
   */
  onCommit: (text: string) => Promise<boolean> | boolean;
}

export function CommitInput({ value, onCommit, onBlur, onKeyDown, ...rest }: Props) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = async () => {
    if (draft === value) return;
    if (!(await onCommit(draft))) setDraft(value);
  };

  return (
    <input
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => { onBlur?.(e); void commit(); }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.key === 'Enter') void commit();
      }}
    />
  );
}
